import type { CategoryTag, IEpisodeInfo, IResRecommendedTheaterItem } from '@/types/apis/theater'
import type { ICreationFeedItem, IRefVideoItem } from './types'
import type { useLikeStore } from '@/stores/like'
import type { useCollectStore } from '@/stores/collect'
import type { IResCreationTaskItem } from '@/types/apis/task'
import { t } from '@/i18n'

export type { FeedPreset, FeedSceneContext, IRefVideoItem } from './types'

type LikeStore = ReturnType<typeof useLikeStore>
type CollectStore = ReturnType<typeof useCollectStore>
export type IWrapEpisodeInfo = IEpisodeInfo & {
  CategoryTags: Array<CategoryTag>
  ScriptIdKey: string
  ScriptName?: string
  ScriptId?: number
  TotalEpisodes: number
}

/**
 * 格式化剧本数据为播放器视频数据
 * @param item 剧集数据（推荐流或剧集详情）
 * @param likeStore 点赞状态管理 store（可选）
 * @param collectStore 收藏状态管理 store（可选）
 */
export const formattedVideoItem = (
  item: IResRecommendedTheaterItem | IWrapEpisodeInfo,
  likeStore?: LikeStore,
  collectStore?: CollectStore,
): IRefVideoItem => {
  const tags = item.CategoryTags.filter(tag => tag.CategoryType.startsWith('Script')).map(i => i.Name)
  const episodeId = item.Id

  // 优先使用 API 返回的状态，否则从 store 查询
  const liked = item.IsLiked ?? (likeStore ? likeStore.isLiked(episodeId) : false)
  const collected = item.IsCollected ?? (collectStore ? collectStore.isCollected(episodeId) : false)

  // 获取 scriptId 和 scriptName
  const scriptId = item.ScriptId ?? 0

  const scriptName = item.ScriptName ? item.ScriptName : 'EpisodeContent' in item ? item.EpisodeContent : ''

  return {
    episodeId,
    scriptId,
    scriptIdKey: item.ScriptIdKey,
    scriptName,
    url: item.VideoUrl,
    title: scriptName,
    tags,
    liked,
    likeCount: item.LikeCount ?? 0,
    collected,
    collectCount: item.CollectionCount ?? 0,
    commented: false,
    commentCount: item.CommentCount ?? 0,
    shareCount: 0,
    episodeIndex: item.SequenceNumber,
    free: item.IsFree,
    requiresLogin: item.RequiresLogin,
    // TODO: 测试阶段数据
    thumbnail:
      item.CoverImageUrl ||
      'https://files.acadsoc.com.cn/uploads/FaceReplace/20260212/9ca2f4e2-d783-427c-95bb-b831ae6b905f.png',

    // 剧集数
    totalEpisodes: item.TotalEpisodes,
    // 描述
    description: item.EpisodeContent || '',
  }
}

export const formatCreationFeedItem = (item: IResCreationTaskItem): ICreationFeedItem => {
  return {
    id: item.Id,
    videoUrl: item.SaveUrl || '',
    title: item.Title || t('component.feed.aiCreation'),
    prompt: item.Prompt,
    coverUrl: item.StartImage || item.EndImage,
    aspectRatio: item.AspectRatio || '9:16',
    status: item.Status,
  }
}

export const toPlayerFeedItem = (item: ICreationFeedItem): IRefVideoItem => {
  return {
    episodeId: item.id,
    scriptId: item.id,
    scriptIdKey: String(item.id),
    scriptName: item.title || t('component.feed.aiCreation'),
    url: item.videoUrl,
    title: item.title || t('component.feed.aiCreation'),
    tags: [t('component.feed.aiCreation'), item.aspectRatio],
    thumbnail: item.coverUrl,
    liked: false,
    likeCount: 0,
    collected: false,
    collectCount: 0,
    commented: false,
    commentCount: 0,
    shareCount: 0,
    episodeIndex: 1,
    free: true,
    requiresLogin: false,
    totalEpisodes: 1,
    description: item.prompt,
    sourceCreationId: item.id,
    historyEnabled: false,
  }
}
